import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  TransactionHeader,
  TransactionDetailsCard,
  CurrencyAmountCard,
  TransactionInfoCard,
  copyToClipboard,
  LoadingPage,
  CompanyRoleBadge,
  Badge,
  FlaggedBadgeIcon,
  TransactionError,
  FallbackCompanyIcon,
} from '@valiant/ui-shared'
import { useGetUserDepositTransactionQuery, useGetCompanyQuery } from '../generated/graphql'
import { toast } from 'sonner'
import { MainLayout } from '../components/layout/MainLayout'

export function DepositTransaction() {
  const { depositId } = useParams()
  const navigate = useNavigate()
  const [, setCopiedField] = useState<string | null>(null)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  // GraphQL query for deposit transaction (user can only see their own)
  const [{ data: depositData, fetching: depositLoading, error: depositError }, refetch] =
    useGetUserDepositTransactionQuery({
      variables: { transactionId: depositId! },
      pause: !depositId,
    })

  const [{ data: companyData, fetching: companyLoading, error: companyError }] = useGetCompanyQuery(
    {
      variables: { companyId: depositData?.getDepositTransaction?.companyId || '' },
      pause: !depositData?.getDepositTransaction?.companyId,
    },
  )

  const depositTransaction = depositData?.getDepositTransaction

  // Track when initial data has been loaded
  useEffect(() => {
    if (depositTransaction && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [depositTransaction, hasInitiallyLoaded])

  // Ref to store the interval ID for cleanup
  const refetchIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Auto-refetch for non-finalized transactions (excluding pending/requested state)
  useEffect(() => {
    // Clear any existing interval
    if (refetchIntervalRef.current) {
      clearInterval(refetchIntervalRef.current)
      refetchIntervalRef.current = null
    }

    // For deposits, we only refetch when approved and potentially still processing
    // Pending is similar to "requested" state, so we exclude it
    const shouldRefetch = depositTransaction && depositTransaction.finalizedDate === null

    if (shouldRefetch) {
      // Refetch every 5 seconds for approved deposits that might still be processing
      refetchIntervalRef.current = setInterval(() => {
        refetch({ requestPolicy: 'network-only' })
      }, 5000)
    }

    // Cleanup function
    return () => {
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current)
        refetchIntervalRef.current = null
      }
    }
  }, [depositTransaction?.status, depositTransaction, refetch])

  const handleCopy = async (text: string, field: string) => {
    const success = await copyToClipboard(text)
    if (success) {
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
      toast.success('Copied to clipboard')
    }
  }

  const badges = useMemo(() => {
    return [
      {
        node: <CompanyRoleBadge tradeType={companyData?.getCompany?.tradeType || ''} />,
        direction: 'LEFT',
      },
      {
        node: (
          <Badge variant="outline" className="text-xs ">
            {depositTransaction?.group}
          </Badge>
        ),
        direction: 'LEFT',
      },
    ]
  }, [companyData, depositTransaction])

  // Loading state - only show on first load
  if ((depositLoading || companyLoading) && !hasInitiallyLoaded) {
    return <LoadingPage />
  }

  // Error state
  if (depositError || !depositTransaction || companyError) {
    return (
      <TransactionError
        message={
          depositError?.message ||
          companyError?.message ||
          "Transaction not found or you don't have permission to view it."
        }
        title="Error Loading Transaction"
      />
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: depositTransaction.company,
          onClick: () => navigate(`/companies/${depositTransaction.companyId}`),
          imageUrl: depositTransaction.companyImageUrl || '',
          fallbackImage: <FallbackCompanyIcon />,
        },
      ]}
      badges={badges}
      subBarOverride={
        <TransactionHeader
          company={depositTransaction.company}
          companyImageUrl={depositTransaction.companyImageUrl || ''}
          companyTradeType={companyData?.getCompany?.tradeType || ''}
          transactionId={depositId || ''}
          status={depositTransaction.status}
          transactionType="DEPOSIT"
          isFlagged={depositTransaction.isFlagged}
          isExternalApi={depositTransaction.isExternalApi}
          showActions={false} // Hide approve/reject actions in swap mode
        />
      }
    >
      <div className="space-y-6">
        {/* Transaction Overview Header */}
        <div className="space-y-4">
          <h2 className="hidden md:block text-xl font-semibold">Transaction Overview</h2>
          <h3 className="text-2xl md:text-4xl font-normal md:font-semibold md:text-main flex gap-2 items-center">
            <div className="md:hidden">
              {depositTransaction.isFlagged ? <FlaggedBadgeIcon /> : null}
            </div>
            Deposit
          </h3>
        </div>

        {/* Transaction Overview - Side by Side Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CurrencyAmountCard
            company={depositTransaction.company}
            companyImage={depositTransaction.companyImageUrl || ''}
            currency={depositTransaction.currency}
            amount={depositTransaction.amount}
            symbol={depositTransaction.symbol}
            chain={depositTransaction.chain}
            label="Deposit"
          />

          <TransactionInfoCard
            user={depositTransaction.user}
            company={depositTransaction.company}
            group={depositTransaction.group}
            decisionBy={depositTransaction.decisionBy || undefined}
            transactionId={depositId || ''}
            creationDate={depositTransaction.creationDate}
            decisionDate={depositTransaction.decisionDate || undefined}
            finalizedDate={depositTransaction.finalizedDate || undefined}
            onCopy={handleCopy}
          />
        </div>

        <TransactionDetailsCard
          title="Details"
          fields={[
            {
              label: 'Deposit Transaction Hash',
              value: depositTransaction.depositTransactionHash,
              fieldKey: 'hash',
            },
            {
              label: 'Deposit Address',
              value: depositTransaction.depositAddress,
              fieldKey: 'address',
            },
          ]}
          onCopy={handleCopy}
          chain={depositTransaction.chain}
        />
      </div>
    </MainLayout>
  )
}
